/**
 * CR-8706: whole-number quantity inputs on NCR Detail (Batch, Reject and
 * Shortage Qty).
 *
 * They used to show `value || 0` and store `parseInt(value)`: clearing one
 * stored NaN (sent to the API as null, "This field may not be null") while the
 * box still showed 0, and typing beside that 0 left "05" on screen -- React
 * does not rewrite a number input whose text is numerically equal to a numeric
 * value, so the value is handed over as a string.
 */

import type { KeyboardEvent } from 'react';

/** Keys a number input accepts that a whole, non-negative quantity can't use. */
const BLOCKED_KEYS = new Set(['-', '+', 'e', 'E', '.', ',']);

/**
 * Props for a quantity input: the arrows stop at 0 and a minus sign, exponent
 * or decimal point can't be typed (CR-8706 rework).
 */
export const QTY_INPUT_PROPS = {
  min: 0,
  step: 1,
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
    if (BLOCKED_KEYS.has(e.key)) e.preventDefault();
  },
};

/**
 * The text to show for a stored quantity: empty when cleared.
 *
 * @param value - the quantity in form data
 * @returns The input's value
 */
export function qtyText(value: unknown): string {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return typeof value === 'string' ? value : '';
}

/**
 * The quantity to store for what was typed: null when the box is empty, so
 * Save names the field instead of sending NaN.
 *
 * @param text - the input's raw value
 * @returns The whole number, or null
 */
export function parseQty(text: string): number | null {
  const n = parseInt(text, 10);
  return Number.isNaN(n) ? null : n;
}
